/**
 * 出图前确认单(2026-07-30)—— **只在有我们判过的项、或会多花钱时才弹**。
 *
 * ═══ 它不是「再确认一次」那种弹窗 ═══════════════════════════════════════════
 * 规则一句话:**我们判过的请他过目,他自己定的不再问一遍。**
 * 所以熟手(自己选了画幅、默认画质)点「生成」= 直接出图,两次点击,路径不被打断;
 * 走「智能画幅」的人多一次确认,而那一次恰恰是他最需要的 —— 他不知道 3:4 意味着什么,
 * 我们在这里告诉他「小红书首图,因为你写了发小红书」。
 *
 * 四种触发,共同点是**不问会亏一张图或亏积分**:
 *   ① 智能画幅  —— 画幅是我们判的,而画幅错了整张图就废了(不可逆、要重付)
 *   ② 渠道词冲突 —— 选了 1:1 却在文案里写「发抖音」,两个说法只能有一个作数
 *   ③ 4K       —— 单张最贵的一档。★不写倍数(0926 修复波 P5):默认档其实是 1K 不是 2K,
 *                 旧文案「默认档(2K)的 2 倍」两处都错;改为直接列各档单价,数字取自价目表。
 *   ④ 组图一次全出(1005)—— 跳过先出封面,一次扣整组的钱(见 wholeGroup)
 *
 * ★「返回改文案」而不是「取消」:用户在这一步反悔,九成是想改那段话,不是想放弃。
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";
import type { QuoteParams } from "@/entities/pricing/api/pricingService";
import { imageVariant, SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { useBindingQuote } from "@/entities/pricing/model/useQuote";
import { grouped } from "@/entities/scene-card/model/creditRates";
import { posterService } from "../../../api/posterService";
import type { Ratio } from "../../../model/free-create/useFreeCreateForm";
import {
  RATIO_COMPOSITION,
  ratioChannelLabel,
  ratioRuleLabel,
} from "../../../model/free-create/ratioComposition";

export interface ConfirmTrigger {
  /** 画幅是我们判的(用户没手选、文案里也没方向词)。 */
  smartRatio: boolean;
  /** 文案里的方向词与已选画幅打架 —— 值 = 文案那一档。 */
  conflictRatio: Ratio | null;
  /** 选了 4K(积分翻倍)。 */
  tier4k: boolean;
  /**
   * ④ 组图「一次全出」(1005):跳过「先出封面」一次扣整组的钱 —— 会多花积分,必须当面确认。
   * 未传 = 不是这种触发。
   */
  wholeGroup?: { count: number } | null;
  /** 用户当前已选画幅(冲突态与兜底展示用)。 */
  currentRatio: Ratio;
  /** 用来判画幅的那段文字(文案 + 特别要求)。 */
  userText: string;
  /** 底栏那个**预估**值(dryRun)。用来判断「价格已更新」;确认卡显示的是正式单的数。 */
  credits: number | null;
  /** 这一单要落的正式报价单参数(sku + 规格档 + 张数)。null = 报不出价。 */
  quoteParams: QuoteParams | null;
  /** 只读回显:画质 / 张数 / 画风。 */
  tierLabel: string;
  countLabel: string;
  styleLabel: string;
}

const RATIOS = Object.keys(RATIO_COMPOSITION) as Ratio[];

/**
 * 画幅示意图 —— 按真实比例画的一个框(2026-07-31「弹框要醒目一些」)。
 *
 * ★为什么加图形而不是把字号调大:这一步要用户判断的是**画幅对不对**,而「3:4」
 *   三个字对没做过设计的人不构成画面感 —— 他不知道 3:4 有多高。一个按真比例画的框
 *   摆在那儿,是竖是方一眼就看出来,这比任何字号都醒目。
 * ★框里那条浅色带画的是「主体大致落在哪」(取自该档法则的几何),不是预览图 ——
 *   不画假版式是 2026-07-30 拍过的板,这里只表达画幅本身的形状。
 */
function RatioGlyph({
  ratio,
  box = 46,
  className,
}: {
  ratio: Ratio;
  /** 最长边像素 —— 尺寸走内联 style,className 覆盖不了,所以做成入参。 */
  box?: number;
  className?: string;
}) {
  const [w, h] = ratio.split(":").map(Number);
  const BOX = box;
  const width = w >= h ? BOX : Math.round((BOX * w) / h);
  const height = h >= w ? BOX : Math.round((BOX * h) / w);
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-[3px] border-2 border-primary bg-primary/10",
        className
      )}
      style={{ width, height }}
      aria-hidden
    >
      <span className="h-1/2 w-1/2 rounded-[2px] bg-primary/35" />
    </span>
  );
}

/**
 * 4K 提示用的各档单价(同一引擎的 1K / 2K / 4K,取自价目表)。
 * 引擎从正式单的 variantKey(`<provider>_<TIER>`,见 imageVariant)里取,不另传。
 * 任一档查不到 → null,由文案退回「单张最贵的一档」,**永不编数**。
 */
function fourKTierPrices(
  book: ReturnType<typeof usePriceBook>["book"],
  params: QuoteParams | null
): Record<"1K" | "2K" | "4K", number> | null {
  if (!book.ready || !params || params.skuCode !== SKU.posterImage || !params.variantKey) {
    return null;
  }
  const provider = params.variantKey.replace(/_[0-9]K$/i, "");
  const out = {} as Record<"1K" | "2K" | "4K", number>;
  for (const tier of ["1K", "2K", "4K"] as const) {
    const unit = book.unit(SKU.posterImage, imageVariant(provider, tier));
    if (unit == null) return null;
    out[tier] = unit;
  }
  return out;
}

export function GenerateConfirmDialog({
  trigger,
  onCancel,
  onConfirm,
}: {
  /** null = 不弹。 */
  trigger: ConfirmTrigger | null;
  onCancel: () => void;
  /**
   * 确认出图 —— 带上用户最终认可的画幅(null = 不改),以及**这张正式报价单的单号**。
   *
   * ★ 单号必须一路带到建单请求里:结算按单兑现,没有单会静默回退到旧的成本 ×10 口径,
   *   用户被按 6.25 倍扣,而两侧日志都干干净净。
   */
  onConfirm: (ratio: Ratio | null, quoteId: string | null) => void;
}) {
  /**
   * ★ 打开确认卡时**落一张正式报价单**,卡上显示的数就是这张单的数。
   *
   * 改之前这里显示的是 `trigger.credits` —— 底栏那个前端自己算的预估。
   * 「用户看的是 A、扣的是 B,而 A 是前端自己算的」正是这一批要解决的问题;
   * 显示预估值等于**没解决**。
   */
  const quote = useBindingQuote(trigger?.quoteParams ?? null, trigger?.credits ?? null);
  const { book } = usePriceBook();
  const tierPrices = fourKTierPrices(book, trigger?.quoteParams ?? null);
  const [inferred, setInferred] = useState<Ratio | null>(null);
  const [inferring, setInferring] = useState(false);
  const [picked, setPicked] = useState<Ratio | null>(null);
  const [editing, setEditing] = useState(false);

  // 智能画幅:这里**自己**调一次 5 选 1,好把结果连同依据摆给用户看。
  // ★与提交侧走的是同一个端点(`/poster/infer-ratio`),不是另判一次 ——
  //   两处各判各的就会出现「确认单说 3:4、出图按 4:5」。
  useEffect(() => {
    if (!trigger?.smartRatio) return;
    let alive = true;
    setInferring(true);
    posterService
      .inferRatio(trigger.userText)
      .then((r) => alive && setInferred(r.ratio as Ratio))
      .catch(() => alive && setInferred(null))
      .finally(() => alive && setInferring(false));
    return () => {
      alive = false;
    };
  }, [trigger?.smartRatio, trigger?.userText]);

  useEffect(() => {
    // 每次重新打开都从干净状态开始(上一次的选择不该粘住)
    if (trigger) {
      setPicked(null);
      setEditing(false);
      setInferred(null);
    }
  }, [trigger]);

  if (!trigger) return null;

  const suggested: Ratio | null =
    picked ??
    trigger.conflictRatio ??
    inferred ??
    (trigger.smartRatio ? null : trigger.currentRatio);
  const why = trigger.conflictRatio
    ? `文案里写了「${ratioChannelLabel(trigger.conflictRatio) ?? trigger.conflictRatio}」那一类的词,与你选的 ${trigger.currentRatio} 不一致`
    : trigger.smartRatio
      ? "按你写的内容判断的"
      : null;

  const title = trigger.conflictRatio
    ? "画幅对不上,按哪个出?"
    : trigger.smartRatio
      ? `按 ${suggested ?? "…"} 出,可以吗?`
      : trigger.wholeGroup
        ? `一次出全组 ${trigger.wholeGroup.count} 张,确认一下`
        : "这一单会多花积分,确认一下";

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{title}</DialogTitle>
          <DialogDescription className="text-[12.5px]">
            下面这几项会花积分、而且出错就得重付 —— 其余都是你自己定的,不再问一遍。
          </DialogDescription>
        </DialogHeader>

        {/* 画幅 —— 我们判的那一项,带依据、一点可改。
            2026-07-31「醒目一些」:主色描边 + 淡主色底 + 左侧按真比例画的画幅示意,
            把这一格从「表单里的一行」提成「这一屏要看的东西」。 */}
        {(trigger.smartRatio || trigger.conflictRatio) && (
          <div className="rounded-lg border-2 border-primary/45 bg-primary/[0.06] p-3">
            <div className="flex items-start gap-3">
              {inferring ? (
                <span className="w-10 shrink-0 text-[12.5px] text-muted-foreground">画幅</span>
              ) : (
                <RatioGlyph ratio={suggested ?? trigger.currentRatio} className="mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                {inferring ? (
                  <span className="flex items-center gap-1.5 text-[13.5px] text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    正在按你写的内容判断…
                  </span>
                ) : (
                  <>
                    <div className="text-[17px] font-bold leading-tight tracking-tight">
                      {suggested ?? trigger.currentRatio}
                      <span className="ml-2 text-[12.5px] font-normal text-muted-foreground">
                        {ratioChannelLabel(suggested ?? trigger.currentRatio)}
                      </span>
                    </div>
                    {why && <div className="mt-0.5 text-[12px] text-muted-foreground">↳ {why}</div>}
                    <div className="mt-1 text-[12px] text-muted-foreground">
                      {ratioRuleLabel(suggested ?? trigger.currentRatio)}
                    </div>
                  </>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-[12px]"
                onClick={() => setEditing((v) => !v)}
              >
                改
              </Button>
            </div>
            {editing && (
              <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border/50 pt-2">
                {/* 改画幅时每档也带示意图 —— 用户是在「选一个形状」,不是在选一串数字 */}
                {RATIOS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setPicked(r);
                      setEditing(false);
                    }}
                    className={cn(
                      "flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12.5px] transition-colors",
                      (suggested ?? trigger.currentRatio) === r
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/60 text-muted-foreground hover:border-primary/50"
                    )}
                  >
                    <RatioGlyph ratio={r} box={16} className="opacity-80" />
                    {r}
                    <span className="text-2xs opacity-70">{ratioChannelLabel(r)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 组图一次全出:说清楚这一步扣的是整组,不是封面那一张 */}
        {trigger.wholeGroup && (
          <div
            className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-[12.5px]"
            data-testid="confirm-whole-group-note"
          >
            这一步<b className="text-foreground">{trigger.wholeGroup.count} 张一起出</b>
            ,按整组扣积分;不先看封面,风格不满意也要全额重出。想省钱可以返回点「先出封面」。
          </div>
        )}

        {/* 4K:唯一理由是贵,所以只说钱 —— 直接列各档单价,不写倍数(倍数写错过两次) */}
        {trigger.tier4k && (
          <div
            className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-[12.5px]"
            data-testid="confirm-4k-note"
          >
            <b className="text-foreground">4K</b>{" "}
            {tierPrices ? (
              <>
                每张 <b className="text-foreground">{tierPrices["4K"]} 积分</b>(1K{" "}
                {tierPrices["1K"]} · 2K {tierPrices["2K"]} 积分/张)
              </>
            ) : (
              "是单张最贵的一档"
            )}
            ,出图也更慢。印刷 / 大屏才需要。
          </div>
        )}

        {/* 其余只读 —— 说清楚"这些不问" */}
        <div className="rounded-lg bg-muted/30 p-3 text-[12.5px] text-muted-foreground">
          其余按你定的来:{trigger.styleLabel} · {trigger.tierLabel} · {trigger.countLabel}
        </div>

        {/* ★「价格已更新」——**只在不一致时出**。
            技术上我们没做错任何事(正式单是权威),但用户会觉得数字被偷改;
            「报价即合同」保的是不偷扣,不是不解释。每次都显示等于噪音,用户会学会无视它。 */}
        {quote.priceChanged && (
          <div className="rounded-lg border border-warning/40 bg-warning/5 p-2.5 text-[12.5px]">
            <b className="text-foreground">价格已更新</b> —— 你开始编辑之后平台调过价,
            这一单按上面这个数结算。
          </div>
        )}
        {/* 连「问哪一张单」都拼不出来(provider/档位没到货)—— 这是页面自己的降级态,
            不是价钱的问题。**照旧允许出图**:把整页提交卡死比没有报价单更糟,
            而这一单会在后端落一条 `poster_task_without_quote` WARN 并被验收判据逮到。
            ★ 但必须**当面说**,不能让用户以为他看到了价。 */}
        {trigger.quoteParams == null && (
          <div className="rounded-lg bg-muted/30 p-2.5 text-[12.5px] text-muted-foreground">
            这一单暂时报不出价(出图参数还没到齐),按实际用量结算。
          </div>
        )}
        {quote.error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-2.5 text-[12.5px]">
            <b className="text-foreground">报不出价</b> —— {quote.error}。
            这一单先不出图(没有报价单的任务结算时对不上账)。
          </div>
        )}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <span className="font-mono text-[14px] font-bold text-foreground">
            {/* ★ 这个数来自**正式那张单**,不是底栏的预估 */}
            {quote.loading ? (
              <span className="inline-block h-4 w-20 animate-pulse rounded bg-muted/60" />
            ) : quote.credits != null ? (
              `${grouped(quote.credits)} 积分`
            ) : (
              ""
            )}
          </span>
          <span className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              返回改文案
            </Button>
            {/*
              ★ 拿不到单号就不许出图 —— **但只在「本来问得出价」的时候**。
                · quoteParams 有 → 说明参数齐了,报价失败是价系统的问题,拒;
                · quoteParams 无 → 页面自己还没拿到 provider/档位,这是降级态,
                  放行(后端软着陆 + WARN + 验收判据②)。
              第一版写成无条件 `!quote.quoteId`,结果 capabilities 一没到货**整页提交就永久卡死** ——
              把「没有报价单」这个问题修成了「根本不能创作」,后者严重得多。
            */}
            <Button
              type="button"
              disabled={
                inferring || (trigger.quoteParams != null && (quote.loading || !quote.quoteId))
              }
              onClick={() => onConfirm(suggested ?? null, quote.quoteId)}
            >
              出图
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
